import type { SessionResponse } from "@numbet/protocol";
import { useEffect, useState, type ReactNode } from "react";
import { Footer, Page, SiteHeader, Toast } from "../components/ui.tsx";
import { GameOver } from "../game/GameOver.tsx";
import { Lobby } from "../game/Lobby.tsx";
import { Table } from "../game/Table.tsx";
import { useRoom } from "../game/useRoom.ts";
import { t } from "../i18n.ts";
import { linkHandler, navigate } from "../router.ts";
import { clearSession, loadSession } from "../session.ts";
import { JoinRoom } from "./Setup.tsx";
function Shell({ children }: { children: ReactNode }) {
  return (
    <>
      <SiteHeader />
      <Page narrow>{children}</Page>
      <Footer />
    </>
  );
}

function Gone() {
  return (
    <Shell>
      <div className="panel stack">
        <p style={{ margin: 0, fontWeight: 800 }}>{t.roomGone}</p>
        <a className="btn btn-primary" href="/" onClick={linkHandler("/")}>
          {t.toHome}
        </a>
      </div>
    </Shell>
  );
}

function ConnectedRoom({ session, onReset }: { session: SessionResponse; onReset: () => void }) {
  const conn = useRoom(session);

  useEffect(() => {
    if (conn.status === "invalid") onReset();
    if (conn.status === "left" || conn.status === "notFound") clearSession(session.code);
    if (conn.status === "left") navigate("/", true);
  }, [conn.status, onReset, session.code]);

  if (conn.status === "notFound") return <Gone />;
  if (conn.status === "replaced") {
    return (
      <Shell>
        <div className="panel stack">
          <p style={{ margin: 0, fontWeight: 800 }}>{t.replaced}</p>
          <button type="button" className="btn btn-primary" onClick={conn.reconnect}>
            {t.reloadHere}
          </button>
        </div>
      </Shell>
    );
  }
  const state = conn.state;
  if (!state) {
    return (
      <Shell>
        <p className="muted" style={{ textAlign: "center" }}>
          {t.connecting}
        </p>
      </Shell>
    );
  }

  const view = state.view;
  let content;
  if (view.phase === "lobby") {
    content = <Lobby code={session.code} view={view} myId={session.playerId} connected={new Set(state.room.connected)} send={conn.send} />;
  } else if (view.phase === "finished") {
    content = <GameOver view={view} myId={session.playerId} send={conn.send} />;
  } else {
    content = <Table code={session.code} conn={conn} myId={session.playerId} />;
  }

  return (
    <>
      {conn.status === "reconnecting" && (
        <div className="banner" role="status" style={{ position: "fixed", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 60 }}>
          {t.reconnecting}
        </div>
      )}
      {content}
      <Toast code={conn.error} />
    </>
  );
}

export function RoomPage({ code }: { code: string }) {
  const [session, setSession] = useState(() => loadSession(code));
  if (!session) return <JoinRoom initialCode={code} fromLink onJoined={setSession} />;
  return (
    <ConnectedRoom
      key={session.token}
      session={session}
      onReset={() => {
        clearSession(code);
        setSession(null);
      }}
    />
  );
}
